import React, { useEffect, useMemo, useState } from "react";
import Editor from "@monaco-editor/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../../axios";

const sampleQuestions = [
  {
    question: "What is the capital of India?",
    questionHin: "",
    options: ["Delhi", "Mumbai", "Kolkata", "Chennai"],
    optionsHin: [],
    answer: "Delhi",
    answerHin: "",
    quesType: "gk",
  },
];

export default function MockTestQuestions() {
  const { courseId, mockTestId } = useParams();
  const [mockTests, setMockTests] = useState([]);
  const [bulkJson, setBulkJson] = useState(
    JSON.stringify(sampleQuestions, null, 2)
  );
  const [loading, setLoading] = useState(false);

  const selectedMockTest = useMemo(
    () => mockTests.find((mockTest) => mockTest._id === mockTestId),
    [mockTests, mockTestId]
  );

  useEffect(() => {
    fetchMockTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchMockTests = async () => {
    try {
      const res = await axios.get(`/mock-tests/?courseId=${courseId}`);
      setMockTests(res.data?.result || res.data?.data || []);
    } catch (error) {
      setMockTests([]);
      toast.error("Mock tests load nahi ho paye");
    }
  };

  const normalizeQuestion = (question) => ({
    ...question,
    options: Array.isArray(question.options)
      ? question.options
      : String(question.options || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
    optionsHin: Array.isArray(question.optionsHin)
      ? question.optionsHin
      : String(question.optionsHin || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
  });

  const handleBulkUpload = async () => {
    setLoading(true);

    try {
      const parsedQuestions = JSON.parse(bulkJson);

      if (!Array.isArray(parsedQuestions) || !parsedQuestions.length) {
        toast.error("JSON array me at least one question hona chahiye");
        return;
      }

      const questions = parsedQuestions.map(normalizeQuestion);
      const invalidIndex = questions.findIndex(
        (question) =>
          !question.question ||
          !question.answer ||
          !question.quesType ||
          !Array.isArray(question.options) ||
          question.options.length < 2
      );

      if (invalidIndex !== -1) {
        toast.error(`Question ${invalidIndex + 1} me required fields missing hain`);
        return;
      }

      await axios.post("/mock-tests/questions/bulk", {
        mockTest: mockTestId,
        course: courseId,
        questions,
      });

      toast.success(`${questions.length} questions added successfully`);
      setBulkJson(JSON.stringify(sampleQuestions, null, 2));
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Bulk upload failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bgWhite p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-oswald text-2xl font-bold text-purple">
            Add Mock Test Questions
          </h2>
          <p className="text-sm text-gray-600">
            {selectedMockTest?.title || "Selected mock test"} me bulk questions add karein.
          </p>
        </div>

        <Link
          className="rounded border border-bgBlue px-4 py-2 text-sm font-semibold text-bgBlue"
          to={`/dash/mock-tests/${courseId}`}
        >
          Back to Mock Tests
        </Link>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-bgBlue">
              Bulk Question Upload
            </h3>
            <p className="text-sm text-gray-600">
              JSON array paste karke multiple questions ek saath add karein.
            </p>
          </div>
          <button
            type="button"
            className="rounded border border-bgBlue px-4 py-2 text-sm font-semibold text-bgBlue"
            onClick={() => setBulkJson(JSON.stringify(sampleQuestions, null, 2))}
          >
            Fill Sample
          </button>
        </div>

        <div className="overflow-hidden rounded border border-gray-300">
          <Editor
            height="480px"
            defaultLanguage="json"
            theme="vs-dark"
            value={bulkJson}
            onChange={(value) => setBulkJson(value || "")}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "on",
            }}
          />
        </div>

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-gray-600">
            Required fields: question, options, answer, quesType.
          </p>
          <button
            type="button"
            disabled={loading}
            className="rounded bg-green-600 px-5 py-2 font-semibold text-white disabled:opacity-70"
            onClick={handleBulkUpload}
          >
            {loading ? "Uploading..." : "Upload Bulk Questions"}
          </button>
        </div>
      </div>
    </div>
  );
}
